import { useEffect, useState } from "react";

import { Autocomplete, TextField, Typography } from "@mui/material";
import {
	Control,
	Controller,
	FieldPath,
	FieldValues,
	Path,
	RegisterOptions,
	useController,
} from "react-hook-form";

import { getFormError } from "../../../utils";

export type TCommonAutocompleteProps<T extends FieldValues> = {
	control: Control<T>;
	name: Path<T>;
	rules?: Omit<
		RegisterOptions<T, FieldPath<T>>,
		"valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
	>;
	placeholder?: string;
	className?: string;
	options: {
		id: number | string;
		label?: string;
		value: string;
	}[];
	showEndAdornment?: boolean;
};

export function CommonAutocomplete<T extends FieldValues>({
	control,
	name,
	rules,
	placeholder,
	className = "",
	options,
	showEndAdornment = true,
}: TCommonAutocompleteProps<T>) {
	const { field } = useController({ name, control });
	const selectedOption = options.find((option) => option.value === field.value);

	const [inputValue, setInputValue] = useState(
		selectedOption?.label ?? selectedOption?.value ?? "",
	);

	useEffect(() => {
		const selectedOption = options.find(
			(option) => option.value === field.value,
		);
		setInputValue(selectedOption?.label ?? selectedOption?.value ?? "");
	}, [field.value, options]);

	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field: { value, onChange }, formState: { errors } }) => {
				const errorMessage = getFormError(name, errors);

				let newClassName = className;
				if (errorMessage) {
					newClassName += " invalid";
				}

				return (
					<div className="common-autocomplete">
						<Autocomplete
							inputValue={inputValue}
							onInputChange={(e, newInputValue, reason) => {
								if (reason === "clear") {
									setInputValue("");
									return;
								}
								if (reason === "reset") {
									return;
								}
								if (newInputValue === "") {
									onChange("");
								}
								setInputValue(newInputValue);
							}}
							onChange={(e, value) => {
								if (value) {
									onChange(value.value);
									setInputValue(value.value);
								} else {
									onChange("");
									setInputValue("");
								}
							}}
							options={options}
							isOptionEqualToValue={(option1, option2) =>
								option1.id === option2.id
							}
							onBlur={() => {
								const matchOption = options.find(
									(option) => option.label === inputValue,
								);

								if (!matchOption) {
									const previousOption = options.find(
										(option) => option.value === value,
									);

									if (!previousOption) {
										return;
									}

									setInputValue(previousOption.label ?? previousOption.value);
									onChange(value);
									return;
								}

								setInputValue(matchOption.label ?? matchOption.value);
								onChange(matchOption.value);
							}}
							className={newClassName}
							getOptionLabel={(option) => option.label ?? option.value}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder={placeholder}
									slotProps={{
										input: {
											...params.InputProps,
											endAdornment:
												showEndAdornment && params.InputProps.endAdornment,
										},
									}}
								/>
							)}
						/>
						{errorMessage && (
							<Typography className="invalid-message">
								{errorMessage}
							</Typography>
						)}
					</div>
				);
			}}
		/>
	);
}
